import { useEffect, useState } from "react";
import { Pen, CheckCircle2, AlertTriangle, Plug, X } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const PROVIDER_LABELS = {
  dropbox_sign: "Dropbox Sign",
  docusign: "DocuSign",
};

export default function EsignatureSection() {
  const [account, setAccount] = useState({ configured: false });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [provider, setProvider] = useState("dropbox_sign");
  const [saving, setSaving] = useState(false);
  const [consentUrl, setConsentUrl] = useState(null);

  // Dropbox Sign form
  const [dsApiKey, setDsApiKey] = useState("");
  const [dsTestMode, setDsTestMode] = useState(true);

  // DocuSign form
  const [duIntegrationKey, setDuIntegrationKey] = useState("");
  const [duUserId, setDuUserId] = useState("");
  const [duAccountId, setDuAccountId] = useState("");
  const [duPrivateKey, setDuPrivateKey] = useState("");
  const [duEnv, setDuEnv] = useState("sandbox");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/esignature/account");
      setAccount(r.data || { configured: false });
      if (r.data?.configured) setProvider(r.data.provider);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setDsApiKey(""); setDsTestMode(true);
    setDuIntegrationKey(""); setDuUserId(""); setDuAccountId("");
    setDuPrivateKey(""); setDuEnv("sandbox");
    setConsentUrl(null);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setConsentUrl(null);
    const payload = { provider };
    if (provider === "dropbox_sign") {
      payload.dropbox_sign = { api_key: dsApiKey.trim(), test_mode: dsTestMode };
    } else {
      payload.docusign = {
        integration_key: duIntegrationKey.trim(),
        user_id: duUserId.trim(),
        account_id: duAccountId.trim(),
        private_key: duPrivateKey.trim(),
        environment: duEnv,
      };
    }
    try {
      const r = await api.put("/esignature/account", payload);
      if (r.data?.consent_required && r.data?.consent_url) {
        setConsentUrl(r.data.consent_url);
        toast.warning("Saved — but DocuSign consent is required. Open the link below, approve, then send a lease to test.");
      } else {
        toast.success(`${PROVIDER_LABELS[provider]} connected`);
      }
      setEditing(false);
      resetForm();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save credentials");
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!confirm(`Disconnect ${PROVIDER_LABELS[account.provider] || "your e-signature provider"}? Existing envelopes already in flight will keep working.`)) return;
    try {
      await api.delete("/esignature/account");
      toast.success("Disconnected");
      setAccount({ configured: false });
    } catch (err) {
      toast.error("Could not disconnect");
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="esignature-section">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 bg-indigo-600 text-white flex items-center justify-center rounded-sm">
          <Pen className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:"Outfit"}}>E-Signature</h2>
          <p className="text-sm text-slate-500">Connect your own Dropbox Sign or DocuSign account. Send a lease for signature in one click; signed PDFs are stored on the lease record automatically.</p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : account.configured && !editing ? (
        <ConnectedCard account={account} onEdit={() => setEditing(true)} onDisconnect={disconnect} />
      ) : (
        <form onSubmit={save} className="space-y-5">
          <div className="flex gap-2">
            <ProviderTab active={provider === "dropbox_sign"} onClick={() => setProvider("dropbox_sign")} testid="esign-provider-dropbox">
              Dropbox Sign
            </ProviderTab>
            <ProviderTab active={provider === "docusign"} onClick={() => setProvider("docusign")} testid="esign-provider-docusign">
              DocuSign
            </ProviderTab>
          </div>

          {provider === "dropbox_sign" ? (
            <DropboxSignForm
              apiKey={dsApiKey} setApiKey={setDsApiKey}
              testMode={dsTestMode} setTestMode={setDsTestMode}
            />
          ) : (
            <DocuSignForm
              integrationKey={duIntegrationKey} setIntegrationKey={setDuIntegrationKey}
              userId={duUserId} setUserId={setDuUserId}
              accountId={duAccountId} setAccountId={setDuAccountId}
              privateKey={duPrivateKey} setPrivateKey={setDuPrivateKey}
              env={duEnv} setEnv={setDuEnv}
            />
          )}

          {consentUrl ? (
            <div className="border border-amber-300 bg-amber-50 text-amber-900 text-xs rounded-sm p-3 flex items-start gap-2" data-testid="esign-consent-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold mb-1">DocuSign consent required (one-time)</div>
                <div>Open this URL in a new tab while signed in to DocuSign and click <span className="font-semibold">Accept</span>:</div>
                <a className="text-indigo-700 break-all underline" target="_blank" rel="noopener noreferrer" href={consentUrl}>{consentUrl}</a>
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <button
              type="submit"
              className="btn-primary-dark text-sm flex items-center gap-2"
              disabled={saving}
              data-testid="esign-save-button"
            >
              <Plug className="w-4 h-4" /> {saving ? "Verifying…" : "Connect"}
            </button>
            {editing ? (
              <button type="button" onClick={() => { setEditing(false); resetForm(); }} className="text-sm text-slate-500 px-3 py-2 hover:text-slate-900">Cancel</button>
            ) : null}
          </div>
        </form>
      )}
    </section>
  );
}

function ConnectedCard({ account, onEdit, onDisconnect }) {
  const provider = account.provider;
  return (
    <div className="border border-slate-200 rounded-sm p-4" data-testid="esign-connected-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="font-bold text-slate-900">{PROVIDER_LABELS[provider]}</span>
            {provider === "dropbox_sign" && account.dropbox_sign?.test_mode ? (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-sm">Test Mode</span>
            ) : null}
            {provider === "docusign" ? (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm">{account.docusign?.environment}</span>
            ) : null}
            {provider === "docusign" && !account.consent_granted ? (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-sm">Consent pending</span>
            ) : null}
          </div>
          <div className="text-xs text-slate-500 font-mono">
            {provider === "dropbox_sign" ? `API key ${account.dropbox_sign?.api_key_preview}` : null}
            {provider === "docusign" ? (
              <span>
                Integration {account.docusign?.integration_key_preview} · User {account.docusign?.user_id_preview} · Account {account.docusign?.account_id_preview}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} className="text-xs px-2 py-1 border border-slate-200 hover:border-slate-400 rounded-sm" data-testid="esign-edit-button">Edit</button>
          <button onClick={onDisconnect} className="text-xs px-2 py-1 text-rose-600 hover:bg-rose-50 rounded-sm flex items-center gap-1" data-testid="esign-disconnect-button">
            <X className="w-3.5 h-3.5" /> Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}

function ProviderTab({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-sm px-3 py-1.5 rounded-sm border ${active ? "border-indigo-600 text-indigo-700 bg-indigo-50 font-bold" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
    >
      {children}
    </button>
  );
}

function DropboxSignForm({ apiKey, setApiKey, testMode, setTestMode }) {
  return (
    <div className="space-y-3">
      <details className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3">
        <summary className="cursor-pointer font-semibold text-slate-700">How do I get a Dropbox Sign API key?</summary>
        <ol className="list-decimal list-inside mt-2 space-y-1">
          <li>Sign in at <a href="https://sign.dropbox.com" target="_blank" rel="noopener noreferrer" className="text-indigo-700 underline">sign.dropbox.com</a></li>
          <li>Click your avatar (top-right) → <span className="font-semibold">Settings</span> → <span className="font-semibold">API</span> tab</li>
          <li>Click <span className="font-semibold">Create Key</span> (or copy the existing one)</li>
          <li>Paste the 64-character key below</li>
        </ol>
      </details>
      <label className="block text-xs font-semibold text-slate-600">API Key</label>
      <input
        type="password"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        required
        autoComplete="new-password"
        placeholder="Paste your Dropbox Sign API key"
        className="input-clean w-full font-mono text-xs"
        data-testid="esign-dropbox-api-key"
      />
      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
        <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} data-testid="esign-dropbox-test-mode" />
        Test mode (signatures aren't legally binding — recommended while you verify the flow)
      </label>
    </div>
  );
}

function DocuSignForm({ integrationKey, setIntegrationKey, userId, setUserId, accountId, setAccountId, privateKey, setPrivateKey, env, setEnv }) {
  return (
    <div className="space-y-3">
      <details className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3">
        <summary className="cursor-pointer font-semibold text-slate-700">How do I get DocuSign credentials?</summary>
        <ol className="list-decimal list-inside mt-2 space-y-1">
          <li>Sign in at <a href="https://developers.docusign.com" target="_blank" rel="noopener noreferrer" className="text-indigo-700 underline">developers.docusign.com</a> (a free demo account works for sandbox)</li>
          <li>Click <span className="font-semibold">My Apps & Keys</span> in the top-right menu</li>
          <li>Find your <span className="font-semibold">User ID</span> + <span className="font-semibold">API Account ID</span> in the user info card on that page</li>
          <li>Under <span className="font-semibold">Apps & Keys</span> click <span className="font-semibold">Add App</span> — copy the <span className="font-semibold">Integration Key</span></li>
          <li>In the new app, click <span className="font-semibold">Authentication → RSA Keypairs → Generate RSA</span> — copy the <span className="font-semibold">private key</span> (the BEGIN…END PEM block)</li>
          <li>After saving below we will show you a one-time consent URL — open it once, click Accept, then your account is live.</li>
        </ol>
      </details>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Integration Key (Client ID)">
          <input value={integrationKey} onChange={(e) => setIntegrationKey(e.target.value)} required className="input-clean w-full font-mono text-xs" data-testid="esign-docusign-integration-key" />
        </Field>
        <Field label="Environment">
          <select value={env} onChange={(e) => setEnv(e.target.value)} className="input-clean w-full" data-testid="esign-docusign-env">
            <option value="sandbox">Sandbox (developer / demo)</option>
            <option value="production">Production</option>
          </select>
        </Field>
        <Field label="User ID">
          <input value={userId} onChange={(e) => setUserId(e.target.value)} required className="input-clean w-full font-mono text-xs" data-testid="esign-docusign-user-id" />
        </Field>
        <Field label="API Account ID">
          <input value={accountId} onChange={(e) => setAccountId(e.target.value)} required className="input-clean w-full font-mono text-xs" data-testid="esign-docusign-account-id" />
        </Field>
      </div>
      <Field label="RSA Private Key (PEM)">
        <textarea
          value={privateKey}
          onChange={(e) => setPrivateKey(e.target.value)}
          required
          rows={6}
          placeholder={"-----BEGIN RSA PRIVATE KEY-----\n…\n-----END RSA PRIVATE KEY-----"}
          className="input-clean w-full font-mono text-[10px]"
          data-testid="esign-docusign-private-key"
        />
      </Field>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
